// ═══════════════════════════════════════════════════════════════════════════════
//  src/advanced/DebateRoom.jsx
//  FORMATO 05 — SOSTÉN TU POSTURA
//
//  Dos minutos de argumento continuo sobre una postura ASIGNADA, con un
//  contraataque de la IA a mitad de camino.
//
//  ── POR QUÉ LA POSTURA SE ASIGNA ────────────────────────────────────────────
//  Defender lo que uno ya piensa no entrena nada: las palabras están listas de
//  antes. El músculo aparece cuando hay que construir el argumento en vivo, en
//  inglés, sin guion y sin poder parar.
//
//  ── POR QUÉ EL CONTRAATAQUE LLEGA A MITAD ───────────────────────────────────
//  A los sesenta segundos el alumno ya soltó su mejor material y empieza a
//  repetirse. Ahí es donde una entrevista real, una junta real o una defensa de
//  tesis real te interrumpen. Recuperar el hilo después de eso es la habilidad
//  que este formato entrena y que hoy no se entrena en ningún otro sitio de NSL.
//
//  ── LO QUE ES HONESTO DECIR ─────────────────────────────────────────────────
//  El reconocedor del navegador transcribe con errores y normaliza el acento.
//  Por eso el veredicto juzga el ARGUMENTO —si se sostuvo, si tuvo estructura,
//  si contestó al contraataque— y no la pronunciación. El prompt del servidor
//  se lo dice al modelo explícitamente.
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useRef, useState } from 'react';

import { createRecognition, isRecognitionAvailable } from '../utils/browserSpeech';
import { say, stop as stopSpeech, isAvailable as canSpeak } from './advancedSpeech';
import { getCounter, evaluateDebate } from './advancedApi';
import { textosSala } from './salaTextos';

const TARGET_S = 150;      // dos minutos y medio de objetivo
const COUNTER_AT = 60;     // el contraataque llega al primer minuto
const HARD_STOP = 300;     // tope de seguridad del micrófono

// `idioma` lo trae la sala, que ya lo preguntó una vez. Ver ChoiceDrill.
export default function DebateRoom({ topic, side, onExit, onRestart, idioma = 'es' }) {
  const S = textosSala(idioma);
  const T = S.debate;
  const [phase, setPhase] = useState('brief');   // brief|live|grading|verdict|error
  const [elapsed, setElapsed] = useState(0);
  const [transcript, setTranscript] = useState('');
  const [counter, setCounter] = useState(null);
  const [verdict, setVerdict] = useState(null);
  const [error, setError] = useState(null);

  const recRef = useRef(null);
  const tickRef = useRef(null);
  const finalText = useRef('');
  const startedAt = useRef(0);
  const counterFired = useRef(false);
  const sent = useRef(false);

  const canHear = isRecognitionAvailable();

  const cleanup = useCallback(() => {
    stopSpeech();
    try { recRef.current?.stop(); } catch { /* ya detenido */ }
    recRef.current = null;
    clearInterval(tickRef.current);
  }, []);

  useEffect(() => cleanup, [cleanup]);

  /* ── El contraataque ───────────────────────────────────────────────────── */
  const fireCounter = useCallback(async () => {
    if (counterFired.current) return;
    counterFired.current = true;

    try {
      const c = await getCounter({
        debateId: topic.id,
        side,
        transcript: finalText.current,
      });
      setCounter(c);
      if (canSpeak()) say(c.counter_en, { accent: 'en-GB', rate: 1.05 });
    } catch {
      // Un fallo del contraataque no puede tumbar el debate: se sigue hablando.
      setCounter({
        counter_en: 'Keep going — I want to hear how you close that argument.',
        counter_es: T.contraRespaldoEs,
      });
    }
    // `T.contraRespaldoEs` entra en las dependencias: sin él, el respaldo se
    // quedaría con el idioma que hubiera al montar la sala.
  }, [topic, side, T.contraRespaldoEs]);

  /* ── Grabar ────────────────────────────────────────────────────────────── */
  const start = () => {
    if (!canHear) {
      setError(T.sinMicro);
      setPhase('error');
      return;
    }

    setTranscript('');
    finalText.current = '';
    counterFired.current = false;
    sent.current = false;
    setCounter(null);
    setElapsed(0);
    setPhase('live');
    startedAt.current = performance.now();

    const rec = createRecognition({
      onPartial: (t) => { finalText.current = t; setTranscript(t); },
      onFinal: (t) => { finalText.current = t || finalText.current; },
      onError: () => { /* se evalúa con lo que haya */ },
    });
    recRef.current = rec;
    rec?.start();

    tickRef.current = setInterval(() => {
      const s = Math.round((performance.now() - startedAt.current) / 1000);
      setElapsed(s);
      if (s >= COUNTER_AT && !counterFired.current) fireCounter();
      if (s >= HARD_STOP) finish();
    }, 1000);
  };

  const finish = useCallback(async () => {
    if (sent.current) return;
    sent.current = true;

    clearInterval(tickRef.current);
    stopSpeech();
    try { recRef.current?.stop(); } catch { /* ya detenido */ }

    const seconds = (performance.now() - startedAt.current) / 1000;
    setPhase('grading');

    try {
      setVerdict(await evaluateDebate({
        debateId: topic.id, side, transcript: finalText.current, seconds,
      }));
      setPhase('verdict');
    } catch (e) {
      setError(e.message);
      setPhase('error');
    }
  }, [topic, side]);

  /* ── Render ────────────────────────────────────────────────────────────── */
  if (phase === 'error') {
    return (
      <div className="ar-screen">
        <div className="ar-fail">{error}</div>
        <div className="ar-actions">
          <button type="button" className="ar-btn ar-btn-ghost" onClick={onExit}>{S.drill.volverSala}</button>
        </div>
      </div>
    );
  }

  const score = verdict?.score;
  const tone = score === null || score === undefined ? 'ar-v-mid'
    : score >= 70 ? 'ar-v-ok' : score >= 45 ? 'ar-v-mid' : 'ar-v-bad';
  const left = Math.max(0, TARGET_S - elapsed);

  return (
    <div className="ar-screen" style={{ '--ar-mc': 'var(--ar-f-debate)' }}>
      <div className="ar-hud">
        <button type="button" className="ar-quit" onClick={() => { cleanup(); onExit(); }}>{S.drill.salir}</button>
        <span className="ar-badge">{S.formatos.debate.name}</span>
        <span className="ar-spacer" />
        <span className="ar-badge">{topic.level}</span>
      </div>

      <div className="ar-card-q">
        <span className="ar-lbl">{T.laMocion}</span>
        <p className="ar-motion">{topic.motion}</p>
        <p className="ar-scene" style={{ marginTop: '.6rem' }}>{topic.es}</p>
        <span className={`ar-side ${side === 'for' ? 'ar-side-for' : 'ar-side-against'}`}>
          {side === 'for' ? T.defiendes : T.atacas}
        </span>
      </div>

      {phase === 'brief' && (
        <>
          <p className="ar-note">{T.aviso}</p>
          <div className="ar-actions">
            <button type="button" className="ar-btn" onClick={start}>{T.empezar}</button>
            <button type="button" className="ar-btn ar-btn-ghost" onClick={onRestart}>{T.otraMocion}</button>
          </div>
        </>
      )}

      {phase === 'live' && (
        <>
          <p className={`ar-clock ar-num ${left === 0 ? 'ar-hot' : ''}`}>
            <span className="ar-dot" aria-hidden="true" />
            {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}
          </p>
          <p className="ar-note" style={{ textAlign: 'center', marginTop: 0 }}>
            {left > 0
              ? T.objetivo(Math.floor(left / 60), String(left % 60).padStart(2, '0'))
              : T.objetivoHecho}
          </p>

          {counter && (
            <div className="ar-counter">
              <span className="ar-counter-lbl">{T.contraataque}</span>
              <p>{counter.counter_en}</p>
              <small>{counter.counter_es}</small>
            </div>
          )}

          <div className="ar-transcript">{transcript}</div>

          <div className="ar-actions">
            <button type="button" className="ar-btn ar-btn-rec" onClick={finish}>{T.cerrar}</button>
          </div>
        </>
      )}

      {phase === 'grading' && (
        <>
          <div className="ar-spin" />
          <p className="ar-lbl" style={{ textAlign: 'center' }}>{T.leyendo}</p>
        </>
      )}

      {phase === 'verdict' && verdict && (
        <>
          <div className={`ar-verdict ${tone}`} style={{ marginTop: '.9rem' }}>
            {score !== null && score !== undefined
              ? <p className="ar-score ar-num">{Math.round(score)}<small>/100</small></p>
              : <p className="ar-lbl">{T.sinCalificar}</p>}

            <p className="ar-said">{verdict.verdict_es}</p>

            {verdict.strengths?.length > 0 && (
              <ul className="ar-list">
                {verdict.strengths.map((s) => <li key={s} className="ar-plus">{s}</li>)}
              </ul>
            )}
            {verdict.improve?.length > 0 && (
              <ul className="ar-list">
                {verdict.improve.map((s) => <li key={s} className="ar-minus">{s}</li>)}
              </ul>
            )}

            <div className="ar-stats">
              <div><b className="ar-num">{verdict.seconds}s</b><span>{T.sostenido}</span></div>
              <div><b className="ar-num">{verdict.words}</b><span>{T.palabras}</span></div>
              <div><b className="ar-num">{Math.round(verdict.wpm)}</b><span>{T.ritmo}</span></div>
            </div>

            {verdict.ai_evaluated === false && (
              <p className="ar-note">{T.sinIA}</p>
            )}
          </div>

          <div className="ar-actions">
            <button type="button" className="ar-btn" onClick={onRestart}>{T.otraMocionFlecha}</button>
            <button type="button" className="ar-btn ar-btn-ghost" onClick={onExit}>{S.drill.volverSala}</button>
          </div>
        </>
      )}
    </div>
  );
}
